import { css, html, LitElement } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { clusterCss } from '../css/cluster';
import { formCss } from '../css/form';
import { resetCss } from '../css/reset';
import { stackCss } from '../css/stack';
import { typeCss } from '../css/type';
import { debounce } from '../decorators';
import '../freqsElement';
import { wordsToFreqs } from '../letterFrequencies';
import { makeRangeDescription, uniq } from '../util';
import { wordListPromise } from '../wordList';
import '../wordListElement';

function hangmanFilter(words: string[], pattern: string, misses: string) {
  // The letters that have already been guessed (and are therefore not in
  // unknown parts of the solution) are determined by the known parts of
  // `pattern` and all of `misses`.
  const guessed = pattern.replace(/[^a-z]/g, '') + misses;

  // Regular expression character class to be used in place of the unknown
  // (hyphenated) parts of the pattern.
  const unguessedCharClass = `[^${guessed}]`;

  const patternRe = new RegExp(
    `^${pattern.replace(/[^a-z]/g, unguessedCharClass)}$`,
  );

  return words.filter(patternRe.test.bind(patternRe));
}

const difference = <T>(as: T[], bs: T[]): T[] => as.filter(a => !bs.includes(a));
const strDifference = (strA: string, strB: string): string =>
  difference(Array.from(strA), Array.from(strB)).join('');

// Make everything that's not a letter turn into a hyphen.
const normalizePattern = (pattern: string) =>
  pattern.toLowerCase().replace(/[^a-z]/g, '-');

// Only letters make sense --- remove anything else.
const normalizeMisses = (misses: string) => misses.toLowerCase().replace(/[^a-z]/g, '');

@customElement('mw-hangman-page')
export class HangmanPageElement extends LitElement {
  @state()
  pattern = '-u--le';
  @state()
  misses = 'aitb';
  @state()
  words: string[] = [];
  @state()
  freqs: { [key: string]: number; } = {};

  limit = 1000;  // don't render more than this many words at once
  @state()
  rangeDescription = ''; // "first 1000 of 2345"
  @state()
  loading = true;
  @state()
  error = false;

  constructor() {
    super();
    this.freqs = wordsToFreqs([]);  // zero freqs
    this.updateResults();
  }

  @debounce(350)
  updateResults() {
    this.loading = true;
    this.error = false;
    wordListPromise
      .then(wordList => {
        this.words = hangmanFilter(wordList, this.pattern, this.misses);
      }, reason => {
        this.words = [];
        this.error = reason;
      })
      .finally(() => {
        this.loading = false;
        this.rangeDescription =
          makeRangeDescription(this.words.length, this.limit);
        this.freqs = wordsToFreqs(this.words);
      });
  };

  onPatternChange(event: Event) {
    let pattern = (event.target as HTMLInputElement).value;
    pattern = normalizePattern(pattern);
    // Prevent wrongly-guessed letters from becoming part of the solution.
    pattern = strDifference(pattern, this.misses);
    this.pattern = pattern;
    this.updateResults();
  };

  onMissesChange(event: Event) {
    const target = (event.target as HTMLInputElement);
    let misses = target.value;
    misses = normalizeMisses(misses);
    // Prevent letters that are part of the solution from becoming part of
    // the wrongly-guessed letters.
    misses = strDifference(misses, this.pattern);
    // Remove duplicate misses; it only makes sense to guess a letter once.
    misses = uniq(Array.from(misses)).join('');
    this.misses = misses;
    // A hack? to make sure the input's displayed value updates when the new
    // value undoes the change the user just made. Without this, the framework
    // sees "no change" and the user's change isn't undone.
    // https://github.com/Polymer/lit-element/issues/144#issuecomment-468098257
    target.value = this.misses;
    this.updateResults();
  };


  static get styles() {
    return [
      resetCss,
      typeCss,
      formCss,
      stackCss,
      clusterCss,
      css`
        mw-freqs {
            padding-right: 1rem;
            border-right: 1px solid black;
            margin-right: 1rem;
            float: left;
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="stack">
        <p>
          Type in your progress so far, indicating unsolved letters with a
          <samp>-</samp> (hyphen). Also enter the letters that have been guessed but
          aren't in the mystery word.
        </p>

        <div class="cluster">
          <div>
            <label for="pattern">Your progress</label>
            <input
                id="pattern"
                class="puzzle-input"
                autofocus
                spellcheck="false"
                .value="${this.pattern}"
                @input="${this.onPatternChange}">
          </div>

          <div>
            <label for="misses">Guessed letters</label>
            <input
                id="misses"
                class="puzzle-input"
                spellcheck="false"
                .value="${this.misses}"
                @input="${this.onMissesChange}">
          </div>
        </div>

        <div>
          <h2>See the ${this.pattern && this.rangeDescription} possible words</h2>

          ${this.loading ? html`<p>Loading&hellip;</p>` : null}

          ${this.error ? html`
          <p title="${JSON.stringify(this.error)}">
            Something&rsquo;s broken &mdash; we couldn't find your words.
          </p>
          ` : null}

          <mw-freqs .freqs="${this.freqs}"></mw-freqs>
          <mw-word-list .words="${this.words}" .limit="${this.limit}"></mw-word-list>
        </div>
      </div>
    `;
  }
}
